import { DOWNTIMEMETHODCAPTURE } from "constants/constants";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { MQTTBrokerContext } from "contexts/MQTTBrokerContext/MQTTBrokerContext";
import { EventListeningEntity } from "mqtt/entities/EventReport/EventListeningEntity";
import { IPLCEvent } from "mqtt/entities/EventReport/EventReportInterfaces";
import { MqttClient } from "precompiled-mqtt";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { getClusterIdHMI } from "utilities/storageUtilities";

function useMQTTListener(onMessage: (message: IPLCEvent) => void, isPlc: boolean) {
    const client = useRef<MqttClient>();
    // Read onMessage through a ref so a new callback identity does NOT re-run the effect below
    // (which would tear down and rebuild the MQTT client on every parent render).
    const onMessageRef = useRef(onMessage);
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);
    const [isListening, setIsListening] = useState(false);
    const [error, setError] = useState<Error>();
    const { currentSetting } = useContext(MQTTBrokerContext);
    const {
        store: { machineCenter: machineCenterHMI },
    } = useContext(HMICachedDataContext);
    const {
        store: { machineCentersCluster, clusterDetails },
    } = useContext(ClusterCachedDataContext);
    const isCluster = getClusterIdHMI();
    const HMIClusterCode = useMemo(
        () => (isCluster ? clusterDetails?.cluster_code || machineCenterHMI?.hmi_code : machineCenterHMI?.hmi_code),
        [clusterDetails, machineCenterHMI, isCluster],
    );
    const HMIClusterPlcAddress = useMemo(
        () =>
            isCluster
                ? machineCentersCluster
                      ?.filter(
                          (machineCenterCluster) =>
                              machineCenterCluster.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC &&
                              Boolean(machineCenterCluster.plc_address),
                      )
                      ?.map((machineCenterCluster) => machineCenterCluster.plc_address) ||
                  (machineCenterHMI?.plc_address ? [machineCenterHMI?.plc_address] : undefined)
                : machineCenterHMI?.plc_address
                ? [machineCenterHMI?.plc_address]
                : undefined,
        [machineCentersCluster, machineCenterHMI, isCluster],
    );

    useEffect(() => {
        if (isPlc && currentSetting && HMIClusterCode && HMIClusterPlcAddress) {
            // startListening returns synchronously, so we own the single client immediately and
            // the cleanup can always tear it down — no async window where an in-flight client
            // escapes cleanup and leaks a reconnecting socket.
            let listeningClient: MqttClient | undefined;
            try {
                listeningClient = EventListeningEntity.startListening(
                    (message) => onMessageRef.current(message),
                    setError,
                    () => setIsListening(true),
                    HMIClusterPlcAddress as string[],
                    currentSetting,
                    HMIClusterCode,
                );
                client.current = listeningClient;
            } catch (error) {
                // A synchronous failure (e.g. a malformed broker URL) surfaces as error state
                // instead of an uncaught exception thrown out of the effect callback.
                setError(error as Error);
            }
            return () => {
                // Force-close so the socket is released now: a graceful end() can linger while
                // draining, and with reconnectPeriod set it could still attempt a reconnect.
                // Note: isListening is intentionally NOT reset here — it is a sticky "listener
                // established" flag so the HMI's PLC-connected state doesn't flap on reconnects or
                // effect re-runs (real PLC-down is detected via machinePlcDisconnected). A true
                // teardown just unmounts the consumer.
                if (listeningClient) {
                    listeningClient.end(true);
                }
                if (client.current === listeningClient) {
                    client.current = undefined;
                }
            };
        }
        // onMessage is read via onMessageRef, so it is intentionally not a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSetting, isPlc, HMIClusterCode, HMIClusterPlcAddress]);

    return { isListening, error };
}

export default useMQTTListener;
